import { ListRenderItem } from '@shopify/flash-list';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { PageFlatList } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { sortBy } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  CLEAR_CHOSEN_SEARCH_ASSETS,
  CLOSE_CLOUD_ASSETS,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  PAUSE_BG_NETWORK,
  SET_FILTER,
  UNPAUSE_BG_NETWORK,
  UNSET_CLOUD_ASSETS,
  eventSelectScreenCancel,
  eventSelectScreenSelection,
} from 'store/actions';
import {
  selectChosenSearchAssetsAreChosen,
  selectSearchAssetIds,
  selectSearchOpened,
  selectSearchOrganisationFilterOptions,
} from 'store/search/selectors';

import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import { CloudAssetsHeader } from './CloudAssetsHeader';
import { CloudAssetListFooter } from './CloudAssetsListFooter';
import { ListItem } from './ListItem';

const renderItemWithCacheKey =
  (cacheKey: string): ListRenderItem<string> =>
  ({ item }) => <ListItem id={item} cacheKey={cacheKey} />;

export const CloudAssetsScreen: React.FC<
  ModalScreenProps<ModalScreens.CLOUDASSETS>
> = ({ navigation }) => {
  const dispatch = useDispatch();
  const organisationPickerWasShownRef = React.useRef(false);

  const [organisationIds, setOrganisationIds] = React.useState<string[]>([]);
  const eventKeyOrganisationSelect = React.useRef(randomKey());

  const searchOpened = useSelector(selectSearchOpened);
  const assetIds = useSelector(selectSearchAssetIds);
  const assetsAreChosen = useSelector(selectChosenSearchAssetsAreChosen);

  const organisations = useSelector(selectSearchOrganisationFilterOptions);
  const searchShowOrganisationBlock =
    organisations.length > 25 && !organisationIds.length;

  const cacheKey = React.useRef(Date.now().toString());
  const renderItem = React.useMemo(
    () => renderItemWithCacheKey(cacheKey.current),
    [],
  );

  const handleShowOrganisationPicker = React.useCallback(
    () =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            multiSelect: true,
            searchable: true,
            title: 'Organisations',
            text: 'Choose an organisation to find and download assets',
            eventKey: eventKeyOrganisationSelect.current,
            showQuickChoose: true,
            value: organisationIds,
            options: sortBy(Object.values(organisations), ['name']).map(
              (org) => ({
                name: org.name,
                internalValue: org.id,
                groupName: '',
              }),
            ),
          },
        }),
      ),
    [dispatch, organisationIds, organisations],
  );

  const handleClose = React.useCallback(() => {
    if (assetsAreChosen) {
      dispatch(CLEAR_CHOSEN_SEARCH_ASSETS());
      return;
    }
    dispatch(CLOSE_CLOUD_ASSETS());
  }, [assetsAreChosen, dispatch]);

  React.useEffect(() => {
    if (!searchOpened) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, searchOpened]);

  React.useEffect(() => {
    dispatch(PAUSE_BG_NETWORK());
    return () => {
      dispatch(UNSET_CLOUD_ASSETS());
      dispatch(UNPAUSE_BG_NETWORK());
    };
  }, [dispatch]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !assetsAreChosen });
  }, [navigation, assetsAreChosen]);
  useEsc(handleClose);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyOrganisationSelect.current) {
          if (
            !event.payload.stringValues.length &&
            searchShowOrganisationBlock
          ) {
            dispatch(CLOSE_CLOUD_ASSETS());
          } else {
            setOrganisationIds(event.payload.stringValues);
            dispatch(SET_FILTER({ organisations: event.payload.stringValues }));
          }
        }
      }),
      doAddEvent(eventSelectScreenCancel, (event) => {
        if (event.payload.key === eventKeyOrganisationSelect.current) {
          if (searchShowOrganisationBlock) {
            dispatch(CLOSE_CLOUD_ASSETS());
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, searchShowOrganisationBlock]);

  React.useEffect(() => {
    if (searchShowOrganisationBlock) {
      if (!organisationPickerWasShownRef.current) {
        organisationPickerWasShownRef.current = true;
        dispatch(handleShowOrganisationPicker());
      }
    }
  }, [dispatch, handleShowOrganisationPicker, searchShowOrganisationBlock]);

  return React.useMemo(
    () => (
      <PageFlatList
        contentPaddingBottom={0}
        header={
          <CloudAssetsHeader
            onClose={handleClose}
            openOrganisationFilter={handleShowOrganisationPicker}
          />
        }
        emptyConfig={
          searchShowOrganisationBlock
            ? {
                icon: ICONS.CloudSolid,
                button: {
                  text: 'Choose an Organisation',
                  onPress: handleShowOrganisationPicker,
                },
              }
            : undefined
        }
        listConfig={{
          data: assetIds,
          keyExtractor: (item) => item,
          ListFooterComponent: <CloudAssetListFooter />,
          renderItem,
        }}
      />
    ),
    [
      assetIds,
      handleClose,
      handleShowOrganisationPicker,
      renderItem,
      searchShowOrganisationBlock,
    ],
  );
};
